import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coffee, Clock, TimerReset, Download } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

const elapsedMinNow = (iso) => {
  try { return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)); }
  catch { return 0; }
};

export default function BreakHistoryPage() {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().slice(0, 10); })();

  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);
  const [empFilter, setEmpFilter] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({ sessions: [], total_minutes: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (empFilter !== "all") params.employee_id = empFilter;
      const { data: r } = await api.get("/attendance/breaks", { params });
      setData(r);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load break history");
    } finally { setLoading(false); }
  }, [startDate, endDate, empFilter]);

  useEffect(() => {
    api.get("/employees").then(({ data: emps }) => setEmployees(emps.filter(e => e.active))).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadMonthly = async () => {
    setDownloading(true);
    try {
      const [year, month] = (endDate || today).split("-").map(Number);
      const params = { year, month };
      if (empFilter !== "all") params.employee_id = empFilter;
      const res = await api.get("/attendance/breaks/monthly-pdf", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-breaks-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded break report for ${year}-${String(month).padStart(2, "0")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Download failed");
    } finally { setDownloading(false); }
  };

  const totalHours = (data.total_minutes / 60).toFixed(1);

  return (
    <div data-testid="break-history-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Attendance</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3">
            Break History <Coffee className="w-8 h-8 text-primary" aria-hidden="true" />
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Every break session your staff has taken — start time, end time, and duration.
          </p>
        </div>
        <Button
          onClick={downloadMonthly}
          disabled={downloading}
          data-testid="download-monthly-breaks-btn"
          variant="outline"
          className="rounded-full"
        >
          <Download className="w-4 h-4 mr-2" aria-hidden="true" />
          {downloading ? "Preparing…" : "Monthly PDF"}
        </Button>
      </div>

      {/* Filters */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="break-filter-start" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="break-filter-end" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Employee</Label>
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger data-testid="break-filter-employee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <div className="bg-primary text-primary-foreground p-6" data-testid="break-stat-total">
          <div className="text-xs uppercase tracking-widest opacity-70">Total break time</div>
          <div className="mt-3 font-serif text-3xl sm:text-4xl">{totalHours} hrs</div>
          <div className="mt-1 text-xs opacity-70">{data.total_minutes.toFixed(0)} minutes</div>
        </div>
        <div className="bg-white border border-border p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Break sessions</div>
          <div className="mt-3 font-serif text-3xl sm:text-4xl">{data.count}</div>
          <div className="mt-1 text-xs text-muted-foreground">across selected range</div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-border">
        <div className="p-4 border-b border-border flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <Clock className="w-4 h-4" /> Sessions
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : data.sessions.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No breaks recorded here.</p>
            <p className="text-muted-foreground mt-2">Try widening the date range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="break-history-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3 font-normal">Date</th>
                  <th className="px-4 py-3 font-normal">Employee</th>
                  <th className="px-4 py-3 font-normal">Break start</th>
                  <th className="px-4 py-3 font-normal">Break end</th>
                  <th className="px-4 py-3 font-normal text-right">Duration</th>
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s, idx) => (
                  <tr key={`${s.employee_id}-${s.date}-${s.start}-${idx}`} className="border-t border-border">
                    <td className="px-4 py-3">{s.date}</td>
                    <td className="px-4 py-3 font-medium">{s.employee_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtTime(s.start)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {s.open ? <span className="text-primary flex items-center gap-1"><TimerReset className="w-4 h-4"/> On break</span> : fmtTime(s.end)}
                    </td>
                    <td className="px-4 py-3 text-right font-serif text-lg">
                      {s.open ? `${elapsedMinNow(s.start)} min` : `${Math.round(s.minutes)} min`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
